import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Send,
  Mail,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

export default function GestionInvitations() {
  const { currentUser, roles: contextRoles } = useApp();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    prenom: '',
    nom: '',
    role: '',
    message_personnalise: ''
  });
  const [sending, setSending] = useState(false);

  // Load roles directly if not available from context
  useEffect(() => {
    const loadRoles = async () => {
      if (contextRoles && contextRoles.length > 0) {
        setRoles(contextRoles);
      } else {
        try {
          const rolesData = await api.getRoles();
          setRoles(rolesData);
        } catch (error) {
          console.error('Error loading roles:', error);
        }
      }
    };
    loadRoles();
  }, [contextRoles]);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const data = await api.getInvitations();
      setInvitations(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.createInvitation(formData, currentUser.id, `${currentUser.prenom} ${currentUser.nom}`);
      toast.success('Invitation envoyée avec succès');
      setDialogOpen(false);
      setFormData({ email: '', prenom: '', nom: '', role: '', message_personnalise: '' });
      loadInvitations();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedInvitation) return;
    try {
      await api.cancelInvitation(selectedInvitation.id);
      toast.success('Invitation annulée');
      loadInvitations();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    } finally {
      setCancelDialogOpen(false);
      setSelectedInvitation(null);
    }
  };

  const handleResend = async (invitation) => {
    try {
      await api.resendInvitation(invitation.id);
      toast.success('Invitation renvoyée');
      loadInvitations();
    } catch (error) {
      toast.error('Erreur lors du renvoi');
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'pending':
      case 'en_attente':
        return <Badge className="bg-orange-100 text-orange-800"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      case 'accepted':
      case 'acceptee':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Acceptée</Badge>;
      case 'expired':
      case 'expiree':
        return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Expirée</Badge>;
      case 'cancelled':
      case 'annulee':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Annulée</Badge>;
      default:
        return <Badge variant="outline">{statut}</Badge>;
    }
  };

  const stats = {
    total: invitations.length,
    pending: invitations.filter(i => i.statut === 'pending' || i.statut === 'en_attente').length,
    accepted: invitations.filter(i => i.statut === 'accepted' || i.statut === 'acceptee').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Invitations</h1>
          <p className="text-gray-500">Inviter de nouveaux utilisateurs à rejoindre l'application</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          Nouvelle invitation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total envoyées</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Acceptées</p>
                <p className="text-xl font-bold">{stats.accepted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Comment ça marche ?</p>
              <p className="text-sm text-blue-700 mt-1">
                Envoyez une invitation par email. Le destinataire recevra un lien unique pour se connecter 
                avec son compte Microsoft et rejoindre l'application avec le rôle que vous lui attribuez. 
                L'invitation expire après 7 jours.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Invitations envoyées</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucune invitation envoyée</p>
              <Button variant="link" onClick={() => setDialogOpen(true)}>
                Envoyer votre première invitation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle proposé</TableHead>
                  <TableHead>Envoyé par</TableHead>
                  <TableHead>Date d'envoi</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{invitation.role || invitation.role_propose}</Badge>
                    </TableCell>
                    <TableCell>{invitation.cree_par_nom || invitation.envoye_par_nom || 'Admin'}</TableCell>
                    <TableCell>
                      {new Date(invitation.date_creation).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      {invitation.expire_at ? new Date(invitation.expire_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell>{getStatusBadge(invitation.statut)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        {(invitation.statut === 'pending' || invitation.statut === 'en_attente') && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleResend(invitation)}
                              title="Renvoyer"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setSelectedInvitation(invitation);
                                setCancelDialogOpen(true);
                              }}
                              title="Annuler"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Invitation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Nouvelle invitation
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    placeholder="Jean"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Dupont"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="utilisateur@entreprise.fr"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle proposé *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.libelle}>
                        {role.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Message personnalisé (optionnel)</Label>
                <Textarea
                  value={formData.message_personnalise}
                  onChange={(e) => setFormData({ ...formData, message_personnalise: e.target.value })}
                  placeholder="Bienvenue dans l'équipe ! Vous trouverez dans cette application..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={sending} className="gap-2">
                {sending ? (
                  <><RefreshCw className="h-4 w-4 animate-spin" /> Envoi...</>
                ) : (
                  <><Send className="h-4 w-4" /> Envoyer l'invitation</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler l'invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir annuler l'invitation envoyée à <strong>{selectedInvitation?.email}</strong> ?
              Le lien d'invitation ne sera plus valide.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
